import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  ClipboardCheck,
  MapPin,
  Briefcase,
  User,
  Calendar,
  Filter,
} from "lucide-react";
import AttendanceForm from "./AttendanceForm";
import { fetchMyOrganisationSummary } from "./attendanceApi";
import { DepartmentSelect, LocationSelect } from "../../components/common/OrgStructureSelects";
import EmployeeSelect from "../../components/common/EmployeeSelect";
import DatePickerField from "../../components/common/DatePickerField";
import { useEmployees } from "../../hooks/useEmployees";
import { formatEmployeeLabel } from "../../components/common/employeeUtils";
import "../../styles/markAttendance.css";

export default function MarkAttendancePage() {
  const [organisationId, setOrganisationId] = useState("");
  const [department, setDepartment] = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [workDate, setWorkDate] = useState("");
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { employees } = useEmployees();

  const filteredEmployees = useMemo(() => {
    if (!department && !workLocation) return employees;
    return employees.filter((emp) => {
      if (department && String(emp.department_id) !== String(department)) return false;
      if (workLocation && String(emp.location_id) !== String(workLocation)) return false;
      return true;
    });
  }, [employees, department, workLocation]);

  useEffect(() => {
    (async () => {
      try {
        const org = await fetchMyOrganisationSummary();
        setOrganisationId(org?.id || "");
      } catch (e) {
        setError(e.message || "Failed to load organisation");
      }
    })();
  }, []);

  useEffect(() => {
    if (!department && !workLocation) return;
    const match = filteredEmployees.find((e) => String(e.employee_id) === String(employeeId));
    if (employeeId && !match) {
      setEmployeeId("");
      setFiltersApplied(false);
    }
  }, [department, workLocation, filteredEmployees, employeeId]);

  const [filterError, setFilterError] = useState("");

  const handleApplyFilters = () => {
    if (!employeeId || !workDate) {
      setFilterError("Please select an Employee and Work Date to apply filters.");
      return;
    }
    setFilterError("");
    setFiltersApplied(true);
  };

  const handleClearFilters = () => {
    setDepartment("");
    setWorkLocation("");
    setEmployeeId("");
    setWorkDate("");
    setFiltersApplied(false);
    setFilterError("");
  };

  return (
    <div className="mark-att-page px-4 py-4 md:px-6 md:py-6 lg:px-8">
      <div className="mark-att-orb mark-att-orb--a" />
      <div className="mark-att-orb mark-att-orb--b" />
      <div className="mark-att-orb mark-att-orb--c" />
      <div className="mark-att-grid" />

      <div className="mark-att-inner">
        {/* COMPACT FULL-WIDTH BANNER */}
        <div className="mark-att-hero p-4 md:p-5 mb-5">
          <div className="mark-att-hero-shine" />
          {/* Phones: title row on top, actions wrap to a full-width row below. */}
          <div className="relative z-10 flex flex-wrap items-center gap-3">
            <div className="mark-att-icon shrink-0 w-9 h-9 flex items-center justify-center">
              <ClipboardCheck size={20} />
            </div>
            <div className="min-w-0 flex-1 basis-[calc(100%-3rem)] sm:basis-0">
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight drop-shadow-sm">
                Mark Attendance
              </h1>
              <p className="text-xs text-white/80 mt-0.5">
                Record or correct attendance for an employee on a specific work date.
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto shrink-0">
              <Link to="/attendance/records" className="mark-att-link mark-att-link--solid text-xs px-3 py-1.5 flex-1 sm:flex-none justify-center">
                View all records
                <ArrowRight size={12} />
              </Link>
              <Link to="/attendance/apply-calendar" className="mark-att-link mark-att-link--ghost text-xs px-3 py-1.5 flex-1 sm:flex-none justify-center">
                <CalendarDays size={12} />
                Calendar
              </Link>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mb-4 flex items-start gap-2 bg-gradient-to-r from-rose-50 to-red-50 border-l-4 border-rose-500 text-rose-800 rounded-r-lg px-4 py-3 text-sm shadow-sm">
            <span className="font-semibold">{error}</span>
          </div>
        ) : null}

        {/* SINGLE CARD — filter bar + attendance form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm mb-5">
          <div className="grid grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end">
            {/* Work Location */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-1.5">
                <MapPin size={12} className="text-emerald-600" />
                Work Location
              </label>
              <LocationSelect
                hideSelectedHint
                value={workLocation}
                onChange={(val) => {
                  // A chosen location shows employees from all departments.
                  setWorkLocation(val || "");
                  setDepartment("");
                  setFiltersApplied(false);
                }}
              />
            </div>

            {/* Department */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-1.5">
                <Briefcase size={12} className="text-violet-600" />
                Department
              </label>
              <DepartmentSelect
                hideSelectedHint
                value={department}
                onChange={(val) => {
                  // A chosen department shows employees from all locations.
                  setDepartment(val || "");
                  setWorkLocation("");
                  setFiltersApplied(false);
                }}
              />
            </div>

            {/* Employee */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-1.5">
                <User size={12} className="text-indigo-600" />
                Employee <span className="text-rose-500">*</span>
              </label>
              <EmployeeSelect
                hideLabel
                name="filter_employee_id"
                value={employeeId}
                employees={filteredEmployees}
                onChange={(id, emp) => {
                  setEmployeeId(id || "");
                  setFiltersApplied(false);
                  setFilterError("");
                  // Picking an employee fills in their work location and department.
                  const picked = emp || employees.find((e) => String(e.employee_id) === String(id));
                  if (picked) {
                    if (picked.location_id) setWorkLocation(String(picked.location_id));
                    if (picked.department_id) setDepartment(String(picked.department_id));
                  }
                }}
                selectClassName="border border-slate-200 rounded-xl px-3.5 py-2.5 w-full text-sm bg-white text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-400"
              />
            </div>

            {/* Work Date */}
            <div>
              <label className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 mb-1.5">
                <Calendar size={12} className="text-sky-600" />
                Work Date <span className="text-rose-500">*</span>
              </label>
              <DatePickerField
                hideLabel
                value={workDate}
                onChange={(date) => {
                  setWorkDate(date || "");
                  setFiltersApplied(false);
                  setFilterError("");
                }}
                placeholder="Select work date"
              />
            </div>

            {/* Apply & Clear buttons */}
            <div className="flex items-center gap-2 col-span-2 lg:col-span-1">
              <button
                type="button"
                onClick={handleApplyFilters}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-md transition-all duration-200 whitespace-nowrap ${
                  filtersApplied
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 hover:shadow-lg hover:-translate-y-0.5"
                    : "bg-gradient-to-r from-[#0f2d52] to-[#1a437d] hover:shadow-lg hover:-translate-y-0.5"
                }`}
              >
                <Filter size={14} />
                {filtersApplied ? "Applied" : "Apply"}
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition whitespace-nowrap"
              >
                Clear
              </button>
            </div>
          </div>

          {filterError ? (
            <p className="mt-2 text-sm text-rose-600 font-medium">{filterError}</p>
          ) : null}

          {/* Divider between filter and form */}
          <div className="mt-4 mb-4 border-t border-slate-100" />

          {/* ATTENDANCE FORM — inline inside same card */}
          <AttendanceForm
            reload={() => {}}
            defaultOrganisationId={organisationId}
            hideOrganisationId
            hideEmployeeField
            hideDateField
            inline
            externalEmployeeId={filtersApplied ? employeeId : ""}
            externalAttendanceDate={filtersApplied ? workDate : ""}
            employeeName={formatEmployeeLabel(filteredEmployees.find((emp) => String(emp.employee_id) === String(employeeId))) || "this employee"}
            onSaveSuccess={() => navigate("/attendance/records")}
          />
        </div>
      </div>
    </div>
  );
}
